import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAIBrandProfiles,
  useBrandProfileCreateGate,
  useDeleteAIBrandProfile,
  useSetDefaultAIBrandProfile,
  useActivateAIBrandProfile,
  useDuplicateAIBrandProfile,
  useBrandProfileDraft,
  useDeleteBrandProfileDraft,
} from '@/hooks/useAIBrandProfiles';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useTimezone } from '@/contexts/TimezoneContext';
import { AIBrandProfileCreateModal } from '@/components/ai-brand-profile/AIBrandProfileCreateModal';
import { AIBrandProfileEditModal } from '@/components/ai-brand-profile/AIBrandProfileEditModal';
import { AIBrandProfileVersionHistoryModal } from '@/components/ai-brand-profile/AIBrandProfileVersionHistoryModal';
import type {
  AIBrandProfile,
  CreateAIBrandProfileInput,
  AIBrandProfileFormData,
  Audience,
  ProductService,
  Competitor,
  CoreValue,
  AvoidTopic,
  PricePositioning,
  EmotionalTone,
  CompetitorMentionPolicy,
  HashtagStyle,
  DefaultLength,
  WritingStyle,
} from '@/types/ai';
import {
  DEFAULT_PROFILE_FORM_DATA,
  PRICE_POSITIONING_OPTIONS,
  EMOTIONAL_TONE_OPTIONS,
  COMPETITOR_POLICY_OPTIONS,
  TONE_OPTIONS,
  LENGTH_OPTIONS,
  HASHTAG_STYLE_OPTIONS,
  WRITING_STYLE_SENTENCE_LENGTH_OPTIONS,
  BRAND_PERSONALITY_TRAITS,
  VOICE_CHARACTERISTICS_OPTIONS,
} from '@/types/ai';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Modal } from '@/components/ui/Modal';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';

/**
 * AIBrandProfileSettings Component
 *
 * Manages AI Brand Profiles (CRUD) for consistent brand voice across AI-generated content.
 * Features:
 * - List all brand profiles for the team as cards
 * - Create new profiles with 6-step detailed wizard
 * - Edit existing profiles (tabbed interface)
 * - Delete profiles with confirmation
 * - Set default profile
 * - Maximum 10 profiles limit
 */

// ============================================================================
// Helper Components
// ============================================================================

/**
 * TagInput - Component for managing string arrays
 */
interface TagInputProps {
  label: string;
  placeholder?: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  helperText?: string;
}

const TagInput = ({ label, placeholder, tags, onChange, helperText }: TagInputProps) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (!tags.includes(inputValue.trim())) {
        onChange([...tags, inputValue.trim()]);
      }
      setInputValue('');
    }
  };

  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <Input
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {helperText && <p className="mt-1 text-xs text-gray-500">{helperText}</p>}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(index)}
                className="hover:text-primary-900"
              >
                <Icon name={ICONS.CROSS} size="xs" ariaHidden />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * InfoTooltip - Info icon with tooltip for field explanations
 */
interface InfoTooltipProps {
  content: string;
}

const InfoTooltip = ({ content }: InfoTooltipProps) => (
  <Tooltip content={content} side="top">
    <span className="ml-1 inline-flex cursor-help text-gray-400 hover:text-gray-500">
      <Icon name={ICONS.INFO} size="xs" ariaHidden />
    </span>
  </Tooltip>
);

/**
 * LabelWithTooltip - Label with info icon tooltip
 */
interface LabelWithTooltipProps {
  label: string;
  tooltip: string;
  required?: boolean;
}

const LabelWithTooltip = ({ label, tooltip, required }: LabelWithTooltipProps) => (
  <div className="flex items-center">
    <span className="text-sm font-medium text-gray-700">
      {label}{required && ' *'}
    </span>
    <InfoTooltip content={tooltip} />
  </div>
);

/**
 * AudienceCard - Expandable card for audience details
 */
interface AudienceCardProps {
  audience: Audience;
  onEdit: (audience: Audience) => void;
  onDelete: (id: string) => void;
}

const AudienceCard = ({ audience, onEdit, onDelete }: AudienceCardProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white hover:border-primary-200 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-gray-900">{audience.name}</h4>
          {audience.territories.length > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">
              {audience.territories.join(', ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
          >
            <Icon
              name={expanded ? ICONS.ARROW_UP : ICONS.ARROW_DOWN}
              size="xs"
              ariaHidden
            />
          </button>
          <button
            onClick={() => onEdit(audience)}
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
          >
            <Icon name={ICONS.EDIT} size="xs" ariaHidden />
          </button>
          <button
            onClick={() => onDelete(audience.id)}
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-red-600 transition-colors"
          >
            <Icon name={ICONS.TRASH} size="xs" ariaHidden />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
          {audience.demographics && (
            <div>
              <p className="text-xs font-medium text-gray-700">{t('settings:settings_ai_profile_demographics')}</p>
              <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                {audience.demographics.ageRange && <p>{t('settings:settings_ai_profile_age')}: {audience.demographics.ageRange}</p>}
                {audience.demographics.gender && <p>{t('settings:settings_ai_profile_gender')}: {audience.demographics.gender}</p>}
                {audience.demographics.incomeLevel && <p>{t('settings:settings_ai_profile_income')}: {audience.demographics.incomeLevel}</p>}
                {audience.demographics.education && <p>{t('settings:settings_ai_profile_education')}: {audience.demographics.education}</p>}
              </div>
            </div>
          )}
          {audience.psychographics && (
            <div>
              <p className="text-xs font-medium text-gray-700">{t('settings:settings_ai_profile_psychographics')}</p>
              <div className="text-xs text-gray-600 mt-1 space-y-1">
                {audience.psychographics.interests.length > 0 && (
                  <p>{t('settings:settings_ai_profile_interests')}: {audience.psychographics.interests.join(', ')}</p>
                )}
                {audience.psychographics.painPoints.length > 0 && (
                  <p>{t('settings:settings_ai_profile_pain_points')}: {audience.psychographics.painPoints.join(', ')}</p>
                )}
              </div>
            </div>
          )}
          {audience.preferredPlatforms.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-700">{t('settings:settings_ai_profile_platforms')}</p>
              <p className="text-xs text-gray-600 mt-1">{audience.preferredPlatforms.join(', ')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Simple card components for other array types
 */
interface SimpleCardProps {
  item: ProductService | Competitor | CoreValue;
  onDelete: (id: string) => void;
}

const ProductServiceCard = ({ item, onDelete }: SimpleCardProps) => {
  const product = item as ProductService;
  return (
    <div className="flex items-start justify-between border border-gray-200 rounded-lg p-3 bg-white">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-gray-900">{product.name}</h4>
        {product.description && (
          <p className="text-xs text-gray-500 mt-0.5">{product.description}</p>
        )}
        {product.category && (
          <span className="inline-block mt-1 text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
            {product.category}
          </span>
        )}
      </div>
      <button
        onClick={() => onDelete(product.id)}
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-red-600 transition-colors"
      >
        <Icon name={ICONS.TRASH} size="xs" ariaHidden />
      </button>
    </div>
  );
};

const CompetitorCard = ({ item, onDelete }: SimpleCardProps) => {
  const competitor = item as Competitor;
  return (
    <div className="flex items-start justify-between border border-gray-200 rounded-lg p-3 bg-white">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-gray-900">{competitor.name}</h4>
        {competitor.positioning && (
          <p className="text-xs text-gray-500 mt-0.5">{competitor.positioning}</p>
        )}
      </div>
      <button
        onClick={() => onDelete(competitor.name)}
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-red-600 transition-colors"
      >
        <Icon name={ICONS.TRASH} size="xs" ariaHidden />
      </button>
    </div>
  );
};

const CoreValueCard = ({ item, onDelete }: SimpleCardProps) => {
  const value = item as CoreValue;
  return (
    <div className="flex items-start justify-between border border-gray-200 rounded-lg p-3 bg-white">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-gray-900">{value.value}</h4>
        {value.description && (
          <p className="text-xs text-gray-500 mt-0.5">{value.description}</p>
        )}
      </div>
      <button
        onClick={() => onDelete(value.value)}
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-red-600 transition-colors"
      >
        <Icon name={ICONS.TRASH} size="xs" ariaHidden />
      </button>
    </div>
  );
};

const AvoidTopicCard = ({ topic, onDelete }: { topic: AvoidTopic; onDelete: (topic: string) => void }) => {
  return (
    <div className="flex items-start justify-between border border-gray-200 rounded-lg p-3 bg-white">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-gray-900">{topic.topic}</h4>
        {topic.reason && (
          <p className="text-xs text-gray-500 mt-0.5">{topic.reason}</p>
        )}
      </div>
      <button
        onClick={() => onDelete(topic.topic)}
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-red-600 transition-colors"
      >
        <Icon name={ICONS.TRASH} size="xs" ariaHidden />
      </button>
    </div>
  );
};

/**
 * PersonalityTraitsPicker - Grouped multi-select for brand personality
 */
interface PersonalityTraitsPickerProps {
  selected: string[];
  onChange: (selected: string[]) => void;
}

const PersonalityTraitsPicker = ({ selected, onChange }: PersonalityTraitsPickerProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const toggleTrait = (trait: string) => {
    if (selected.includes(trait)) {
      onChange(selected.filter((item) => item !== trait));
    } else {
      onChange([...selected, trait]);
    }
  };

  return (
    <div className="space-y-4">
      {Object.entries(BRAND_PERSONALITY_TRAITS).map(([category, traits]) => (
        <div key={category}>
          <h5 className="text-xs font-semibold text-gray-700 uppercase mb-2">{t(`settings:settings_ai_opt_traitcat_${category}`)}</h5>
          <div className="grid grid-cols-2 gap-2">
            {traits.map((trait) => (
              <label
                key={trait.value}
                className={cn(
                  'flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors',
                  selected.includes(trait.value)
                    ? 'border-primary-300 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <Checkbox
                  checked={selected.includes(trait.value)}
                  onCheckedChange={() => toggleTrait(trait.value)}
                  inline
                />
                <span className="text-sm text-gray-700">{t(`settings:settings_ai_opt_trait_${trait.value}`)}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * WritingStyleForm - Checkboxes for writing style preferences
 */
interface WritingStyleFormProps {
  writingStyle: WritingStyle;
  onChange: (style: WritingStyle) => void;
}

const WritingStyleForm = ({ writingStyle, onChange }: WritingStyleFormProps) => {
  const { t } = useTranslation(['settings', 'common']);
  return (
    <div className="space-y-4">
      <Select
        label={t('settings:settings_ai_form_label_sentence_length')}
        options={WRITING_STYLE_SENTENCE_LENGTH_OPTIONS.map((o) => ({ ...o, label: t(`settings:settings_ai_opt_sentence_${o.value}`) }))}
        value={writingStyle.sentenceLength || 'varied'}
        onValueChange={(value) => onChange({ ...writingStyle, sentenceLength: value as WritingStyle['sentenceLength'] })}
      />

      <div className="space-y-3">
        <label className="flex items-center gap-3">
          <Checkbox
            checked={writingStyle.useActiveVoice ?? true}
            onCheckedChange={(checked) => onChange({ ...writingStyle, useActiveVoice: checked })}
            inline
          />
          <span className="text-sm text-gray-700">{t('settings:settings_ai_profile_use_active_voice')}</span>
        </label>

        <label className="flex items-center gap-3">
          <Checkbox
            checked={writingStyle.useContractions ?? false}
            onCheckedChange={(checked) => onChange({ ...writingStyle, useContractions: checked })}
            inline
          />
          <span className="text-sm text-gray-700">{t('settings:settings_ai_profile_use_contractions')}</span>
        </label>

        <label className="flex items-center gap-3">
          <Checkbox
            checked={writingStyle.useMetaphors ?? false}
            onCheckedChange={(checked) => onChange({ ...writingStyle, useMetaphors: checked })}
            inline
          />
          <span className="text-sm text-gray-700">{t('settings:settings_ai_profile_use_metaphors')}</span>
        </label>
      </div>
    </div>
  );
};

// ============================================================================
// Mapping Functions
// ============================================================================

/**
 * Convert form data to API input
 */
export const mapFormDataToInput = (formData: AIBrandProfileFormData): CreateAIBrandProfileInput => {
  return {
    name: formData.name.trim(),
    companyName: formData.companyName.trim() || undefined,
    industry: formData.industry.trim() || undefined,
    missionStatement: formData.missionStatement.trim() || undefined,
    visionStatement: formData.visionStatement.trim() || undefined,
    brandStory: formData.brandStory.trim() || undefined,
    uniqueSellingProposition: formData.uniqueSellingProposition.trim() || undefined,
    tagline: formData.tagline.trim() || undefined,
    audiences: formData.audiences.length > 0 ? formData.audiences : undefined,
    productsServices: formData.productsServices.length > 0 ? formData.productsServices : undefined,
    valueProposition: formData.valueProposition.trim() || undefined,
    keyDifferentiators: formData.keyDifferentiators.length > 0 ? formData.keyDifferentiators : undefined,
    competitors: formData.competitors.length > 0 ? formData.competitors : undefined,
    pricePositioning: formData.pricePositioning || undefined,
    coreValues: formData.coreValues.length > 0 ? formData.coreValues : undefined,
    brandPersonality: formData.brandPersonality.length > 0 ? formData.brandPersonality : undefined,
    brandPrinciples: formData.brandPrinciples.trim() || undefined,
    brandVoice: formData.brandVoice.trim() || undefined,
    voiceCharacteristics: formData.voiceCharacteristics.length > 0 ? formData.voiceCharacteristics : undefined,
    emotionalTone: formData.emotionalTone || undefined,
    writingStyle: Object.keys(formData.writingStyle).length > 0 ? formData.writingStyle : undefined,
    tone: formData.tone.trim() || undefined,
    formality: formData.formality,
    language: formData.language.trim() || undefined,
    avoidWords: formData.avoidWords.length > 0 ? formData.avoidWords : undefined,
    avoidTopics: formData.avoidTopics.length > 0 ? formData.avoidTopics : undefined,
    sensitiveSubjects: formData.sensitiveSubjects.length > 0 ? formData.sensitiveSubjects : undefined,
    competitorMentionPolicy: formData.competitorMentionPolicy || undefined,
    contentRestrictions: formData.contentRestrictions.trim() || undefined,
    defaultLength: formData.defaultLength || undefined,
    includeEmojis: formData.includeEmojis,
    includeHashtags: formData.includeHashtags,
    hashtagStyle: formData.hashtagStyle || undefined,
    defaultCta: formData.defaultCta.trim() || undefined,
    samplePosts: formData.samplePosts.length > 0 ? formData.samplePosts : undefined,
  };
};

/**
 * Convert API profile to form data
 */
export const mapProfileToFormData = (profile: AIBrandProfile): AIBrandProfileFormData => {
  return {
    name: profile.name,
    companyName: profile.companyName || '',
    industry: profile.industry || '',
    missionStatement: profile.missionStatement || '',
    visionStatement: profile.visionStatement || '',
    brandStory: profile.brandStory || '',
    uniqueSellingProposition: profile.uniqueSellingProposition || '',
    tagline: profile.tagline || '',
    audiences: profile.audiences || [],
    productsServices: profile.productsServices || [],
    valueProposition: profile.valueProposition || '',
    keyDifferentiators: profile.keyDifferentiators || [],
    competitors: profile.competitors || [],
    pricePositioning: profile.pricePositioning || 'mid-market',
    coreValues: profile.coreValues || [],
    brandPersonality: profile.brandPersonality || [],
    brandPrinciples: profile.brandPrinciples || '',
    brandVoice: profile.brandVoice || '',
    voiceCharacteristics: profile.voiceCharacteristics || [],
    emotionalTone: profile.emotionalTone || '',
    writingStyle: profile.writingStyle || {},
    tone: profile.tone || '',
    formality: profile.formality ?? 5,
    language: profile.language || 'English',
    avoidWords: profile.avoidWords || [],
    avoidTopics: profile.avoidTopics || [],
    sensitiveSubjects: profile.sensitiveSubjects || [],
    competitorMentionPolicy: profile.competitorMentionPolicy || 'never_mention',
    contentRestrictions: profile.contentRestrictions || '',
    defaultLength: profile.defaultLength || 'medium',
    includeEmojis: profile.includeEmojis,
    includeHashtags: profile.includeHashtags,
    hashtagStyle: profile.hashtagStyle || 'mixed',
    defaultCta: profile.defaultCta || '',
    samplePosts: profile.samplePosts || [],
  };
};

// ============================================================================
// Create Profile Wizard (6 Steps)
// ============================================================================

export interface ProfileWizardProps {
  formData: AIBrandProfileFormData;
  onChange: (formData: AIBrandProfileFormData) => void;
  step: number;
  onStepChange: (step: number) => void;
  /** Create mode's final step calls this with an explicit status ('DRAFT' | 'ACTIVE'); edit mode calls it with no argument. */
  onSubmit: (status?: 'DRAFT' | 'ACTIVE') => void;
  onCancel: () => void;
  isSubmitting: boolean;
  /** Mode determines button labels and behavior */
  mode?: 'create' | 'edit';
  /** Called when user explicitly wants to discard the saved server-side draft (create mode only) */
  onDiscard?: () => void;
  /** Whether there is a saved server-side draft for this wizard (create mode only) */
  hasDraft?: boolean;
  /** Called when user explicitly saves the current draft and closes the wizard (create mode only) */
  onSaveDraftAndClose?: () => void;
  /** Status of the background autosave-to-server, surfaced as an inline indicator (create mode only) */
  draftSaveStatus?: 'idle' | 'saving' | 'saved' | 'error';
}

export const WIZARD_STEPS = [
  { id: 'setup', label: 'Brand Setup' },
  { id: 'audiences', label: 'Audiences' },
  { id: 'offering', label: 'Offering' },
  { id: 'values', label: 'Values' },
  { id: 'voice', label: 'Voice' },
  { id: 'guidelines', label: 'Guidelines' },
];

export const STEP_DESCRIPTIONS = [
  'Start with the basics — tell us about your brand identity.',
  "Define who you're talking to and their characteristics.",
  'What do you offer and how do you position yourself?',
  'What does your brand stand for? Define your values and personality.',
  'How should your brand sound? Define tone and writing style.',
  'Set boundaries — what to avoid and how to handle competitors.',
];

/**
 * Localized wizard step labels + descriptions (WIZARD_STEPS keeps the ids/count).
 */
export const useBrandProfileWizardSteps = (): {
  steps: { id: string; label: string }[];
  descriptions: string[];
} => {
  const { t } = useTranslation(['settings', 'common']);
  return {
    steps: [
      { id: 'setup', label: t('settings:settings_ai_wizard_step_setup') },
      { id: 'audiences', label: t('settings:settings_ai_wizard_step_audiences') },
      { id: 'offering', label: t('settings:settings_ai_wizard_step_offering') },
      { id: 'values', label: t('settings:settings_ai_wizard_step_values') },
      { id: 'voice', label: t('settings:settings_ai_wizard_step_voice') },
      { id: 'guidelines', label: t('settings:settings_ai_wizard_step_guidelines') },
    ],
    descriptions: [
      t('settings:settings_ai_wizard_desc_setup'),
      t('settings:settings_ai_wizard_desc_audiences'),
      t('settings:settings_ai_wizard_desc_offering'),
      t('settings:settings_ai_wizard_desc_values'),
      t('settings:settings_ai_wizard_desc_voice'),
      t('settings:settings_ai_wizard_desc_guidelines'),
    ],
  };
};

/**
 * ProfileWizardFooter - Fixed footer buttons for the wizard
 */
export const ProfileWizardFooter = ({
  formData,
  step,
  onStepChange,
  onSubmit,
  onCancel,
  isSubmitting,
  mode = 'create',
  onDiscard,
  hasDraft = false,
  onSaveDraftAndClose,
  draftSaveStatus = 'idle',
}: ProfileWizardProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const isEditMode = mode === 'edit';
  const isNextDisabled = step === 0 && !formData.name.trim();

  // Tracks which of the two finish-step buttons ("Save as draft" / "Activate") is
  // in flight, so only that button shows a spinner while `isSubmitting` is shared.
  const [pendingSubmitStatus, setPendingSubmitStatus] = useState<'DRAFT' | 'ACTIVE' | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  useEffect(() => {
    if (!isSubmitting) setPendingSubmitStatus(null);
  }, [isSubmitting]);

  const handleNext = () => {
    if (step < WIZARD_STEPS.length - 1) {
      onStepChange(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      onStepChange(step - 1);
    }
  };

  const handleSubmitWithStatus = (status: 'DRAFT' | 'ACTIVE') => {
    setPendingSubmitStatus(status);
    onSubmit(status);
  };

  return (
    <>
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-3">
        <button
          onClick={step === 0 ? onCancel : handleBack}
          disabled={isSubmitting}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
        >
          {step > 0 && <Icon name={ICONS.ARROW_LEFT} size="sm" ariaHidden />}
          {step === 0 && !isEditMode ? t('settings:settings_ai_profile_close') : step === 0 ? t('settings:settings_ai_profile_cancel') : t('settings:settings_ai_profile_back')}
        </button>

        {/* Save draft & close — explicit, intentional persist-and-leave action (create mode only) */}
        {!isEditMode && onSaveDraftAndClose && (
          <button
            type="button"
            onClick={onSaveDraftAndClose}
            disabled={isSubmitting}
            className="px-3 py-2 rounded-lg text-xs sm:text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Icon name={ICONS.SAVE} size="xs" ariaHidden />
            {t('settings:settings_ai_profile_save_finish_later', { defaultValue: 'Save & finish later' })}
          </button>
        )}

        {/* Inline autosave status indicator (create mode only) */}
        {!isEditMode && draftSaveStatus !== 'idle' && (
          <span
            className={cn(
              'flex items-center gap-1.5 text-xs',
              draftSaveStatus === 'error' ? 'text-red-500' : 'text-gray-400'
            )}
            role="status"
            aria-live="polite"
          >
            {draftSaveStatus === 'saving' && (
              <>
                <Icon name={ICONS.LOADER} size="xs" className="animate-spin" ariaHidden />
                {t('settings:settings_ai_profile_draft_saving')}
              </>
            )}
            {draftSaveStatus === 'saved' && (
              <>
                <Icon name={ICONS.CHECK_CIRCLE} size="xs" ariaHidden />
                {t('settings:settings_ai_profile_draft_saved')}
              </>
            )}
            {draftSaveStatus === 'error' && (
              <>
                <Icon name={ICONS.WARNING} size="xs" ariaHidden />
                {t('settings:settings_ai_profile_draft_save_error')}
              </>
            )}
          </span>
        )}

        {/* Discard draft link — only shown in create mode when a saved draft exists */}
        {!isEditMode && hasDraft && step === 0 && onDiscard && (
          <button
            type="button"
            onClick={() => setShowDiscardConfirm(true)}
            disabled={isSubmitting}
            className="text-xs text-red-500 hover:text-red-700 underline underline-offset-2 disabled:opacity-50"
          >
            {t('settings:settings_ai_profile_discard_draft')}
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        {isEditMode ? (
          <>
            {/* Edit Mode: Save Changes button always visible */}
            <button
              onClick={onSubmit}
              disabled={isSubmitting || !formData.name.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting && <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />}
              {t('settings:settings_ai_profile_save_changes')}
              <Icon name={ICONS.CHECK} size="sm" ariaHidden />
            </button>
            {/* Next button if not on last step */}
            {step < WIZARD_STEPS.length - 1 && (
              <button
                onClick={handleNext}
                disabled={isSubmitting}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
              >
                {t('settings:settings_ai_profile_next')}
                <Icon name={ICONS.ARROW_RIGHT} size="sm" ariaHidden />
              </button>
            )}
          </>
        ) : (
          <>
            {/* Create Mode: Next, or the finish-step "Save as draft" / "Activate" pair */}
            {step < WIZARD_STEPS.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={isNextDisabled || isSubmitting}
                className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {t('settings:settings_ai_profile_next')}
                <Icon name={ICONS.ARROW_RIGHT} size="sm" ariaHidden />
              </button>
            ) : (
              <>
                {/* Secondary: create as a team-visible DRAFT (not selectable for generation) */}
                <button
                  onClick={() => handleSubmitWithStatus('DRAFT')}
                  disabled={isSubmitting || !formData.name.trim()}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting && pendingSubmitStatus === 'DRAFT' && (
                    <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />
                  )}
                  {t('settings:settings_ai_profile_save_as_draft', { defaultValue: 'Save as draft' })}
                </button>
                {/* Primary: create and activate immediately */}
                <button
                  onClick={() => handleSubmitWithStatus('ACTIVE')}
                  disabled={isSubmitting || !formData.name.trim()}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting && pendingSubmitStatus === 'ACTIVE' && (
                    <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />
                  )}
                  {t('settings:settings_ai_profile_activate', { defaultValue: 'Activate' })}
                  <Icon name={ICONS.CHECK} size="sm" ariaHidden />
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>

    {/* Discard draft confirmation */}
    {onDiscard && (
      <ConfirmationModal
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        title={t('settings:settings_ai_profile_discard_draft')}
        description={t('settings:settings_ai_profile_discard_draft_confirm')}
        variant="danger"
        confirmText={t('settings:settings_ai_profile_draft_banner_discard')}
        cancelText={t('common:cancel')}
        onConfirm={() => {
          onDiscard();
          setShowDiscardConfirm(false);
        }}
      />
    )}
    </>
  );
};

/**
 * ProfileWizardContent - Form content for each step (rendered inside WizardModal)
 */
export const ProfileWizardContent = ({
  formData,
  onChange,
  step,
}: Pick<ProfileWizardProps, 'formData' | 'onChange' | 'step'>) => {
  const { t } = useTranslation(['settings', 'common']);
  // Modals for adding complex items
  const [audienceModal, setAudienceModal] = useState(false);
  const [productModal, setProductModal] = useState(false);
  const [competitorModal, setCompetitorModal] = useState(false);
  const [coreValueModal, setCoreValueModal] = useState(false);
  const [avoidTopicModal, setAvoidTopicModal] = useState(false);

  // Temporary state for modals
  const [tempAudience, setTempAudience] = useState<Audience>({
    id: '',
    name: '',
    territories: [],
    preferredPlatforms: [],
  });
  const [tempProduct, setTempProduct] = useState<ProductService>({ id: '', name: '' });
  const [tempCompetitor, setTempCompetitor] = useState<Competitor>({ name: '' });
  const [tempCoreValue, setTempCoreValue] = useState<CoreValue>({ value: '' });
  const [tempAvoidTopic, setTempAvoidTopic] = useState<AvoidTopic>({ topic: '' });

  // Add handlers
  const addAudience = () => {
    if (!tempAudience.name.trim()) return;
    onChange({
      ...formData,
      audiences: [...formData.audiences, { ...tempAudience, id: crypto.randomUUID() }],
    });
    setTempAudience({ id: '', name: '', territories: [], preferredPlatforms: [] });
    setAudienceModal(false);
  };

  const addProduct = () => {
    if (!tempProduct.name.trim()) return;
    onChange({
      ...formData,
      productsServices: [...formData.productsServices, { ...tempProduct, id: crypto.randomUUID() }],
    });
    setTempProduct({ id: '', name: '' });
    setProductModal(false);
  };

  const addCompetitor = () => {
    if (!tempCompetitor.name.trim()) return;
    onChange({
      ...formData,
      competitors: [...formData.competitors, tempCompetitor],
    });
    setTempCompetitor({ name: '' });
    setCompetitorModal(false);
  };

  const addCoreValue = () => {
    if (!tempCoreValue.value.trim()) return;
    onChange({
      ...formData,
      coreValues: [...formData.coreValues, tempCoreValue],
    });
    setTempCoreValue({ value: '' });
    setCoreValueModal(false);
  };

  const addAvoidTopic = () => {
    if (!tempAvoidTopic.topic.trim()) return;
    onChange({
      ...formData,
      avoidTopics: [...formData.avoidTopics, tempAvoidTopic],
    });
    setTempAvoidTopic({ topic: '' });
    setAvoidTopicModal(false);
  };

  return (
    <div key={step} className="animate-in fade-in-0 duration-200 space-y-4">
        {/* STEP 1: Brand Setup */}
        {step === 0 && (
          <>
            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_profile_name')}
                tooltip={t('settings:settings_ai_form_tooltip_give_this_profile_a_descriptive_name')}
                required
              />
              <Input
                placeholder={t('settings:settings_ai_form_placeholder_eg_corporate_linkedin_voice_friendly_instagram')}
                value={formData.name}
                onChange={(e) => onChange({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <LabelWithTooltip
                  label={t('settings:settings_ai_form_label_company_name')}
                  tooltip={t('settings:settings_ai_form_tooltip_the_name_ai_will_use_when')}
                />
                <Input
                  placeholder={t('settings:settings_ai_form_placeholder_eg_acme_inc_nike_spotify')}
                  value={formData.companyName}
                  onChange={(e) => onChange({ ...formData, companyName: e.target.value })}
                />
              </div>
              <div>
                <LabelWithTooltip
                  label={t('settings:settings_ai_form_label_industry')}
                  tooltip={t('settings:settings_ai_form_tooltip_helps_ai_understand_your_market_context')}
                />
                <Input
                  placeholder={t('settings:settings_ai_form_placeholder_eg_saas_ecommerce_real_estate_fitness')}
                  value={formData.industry}
                  onChange={(e) => onChange({ ...formData, industry: e.target.value })}
                />
              </div>
            </div>

            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_tagline')}
                tooltip={t('settings:settings_ai_form_tooltip_a_memorable_phrase_that_captures_your')}
              />
              <Input
                placeholder={t('settings:settings_ai_form_placeholder_eg_think_different_just_do_it')}
                value={formData.tagline}
                onChange={(e) => onChange({ ...formData, tagline: e.target.value })}
              />
            </div>

            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_mission_statement')}
                tooltip={t('settings:settings_ai_form_tooltip_why_your_company_exists_the_purpose')}
              />
              <Textarea
                placeholder={t('settings:settings_ai_form_placeholder_eg_to_empower_small_businesses_to')}
                value={formData.missionStatement}
                onChange={(e) => onChange({ ...formData, missionStatement: e.target.value })}
                rows={2}
              />
            </div>

            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_vision_statement')}
                tooltip={t('settings:settings_ai_form_tooltip_the_future_state_youre_working_to')}
              />
              <Textarea
                placeholder={t('settings:settings_ai_form_placeholder_eg_a_world_where_every_business')}
                value={formData.visionStatement}
                onChange={(e) => onChange({ ...formData, visionStatement: e.target.value })}
                rows={2}
              />
            </div>

            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_brand_story')}
                tooltip={t('settings:settings_ai_form_tooltip_your_origin_story_how_and_why')}
              />
              <Textarea
                placeholder={t('settings:settings_ai_form_placeholder_eg_founded_in_2020_when_our')}
                value={formData.brandStory}
                onChange={(e) => onChange({ ...formData, brandStory: e.target.value })}
                rows={3}
              />
            </div>

            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_unique_selling_proposition_usp')}
                tooltip={t('settings:settings_ai_form_tooltip_what_makes_you_the_only_choice')}
              />
              <Textarea
                placeholder={t('settings:settings_ai_form_placeholder_eg_the_only_platform_that_combines')}
                value={formData.uniqueSellingProposition}
                onChange={(e) => onChange({ ...formData, uniqueSellingProposition: e.target.value })}
                rows={2}
              />
            </div>
          </>
        )}

        {/* STEP 2: Audiences */}
        {step === 1 && (
          <>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">{t('settings:settings_ai_profile_audiences_label')}</label>
              <button
                onClick={() => setAudienceModal(true)}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <Icon name={ICONS.PLUS} size="xs" ariaHidden />
                {t('settings:settings_ai_profile_add_audience')}
              </button>
            </div>

            {formData.audiences.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                <p className="text-sm text-gray-500">{t('settings:settings_ai_profile_audiences_empty')}</p>
                <button
                  onClick={() => setAudienceModal(true)}
                  className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  {t('settings:settings_ai_profile_audiences_first')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.audiences.map((audience) => (
                  <AudienceCard
                    key={audience.id}
                    audience={audience}
                    onEdit={(aud) => {
                      setTempAudience(aud);
                      setAudienceModal(true);
                    }}
                    onDelete={(id) =>
                      onChange({ ...formData, audiences: formData.audiences.filter((a) => a.id !== id) })
                    }
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* STEP 3: Offering */}
        {step === 2 && (
          <>
            {/* Products/Services */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('settings:settings_ai_profile_products_services')}</label>
                  <p className="text-xs text-gray-500 mt-0.5">{t('settings:settings_ai_profile_products_services_hint')}</p>
                </div>
                <button
                  onClick={() => setProductModal(true)}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <Icon name={ICONS.PLUS} size="xs" ariaHidden />
                  {t('settings:settings_ai_profile_add_product')}
                </button>
              </div>
              {formData.productsServices.length > 0 && (
                <div className="space-y-2">
                  {formData.productsServices.map((product) => (
                    <ProductServiceCard
                      key={product.id}
                      item={product}
                      onDelete={(id) =>
                        onChange({
                          ...formData,
                          productsServices: formData.productsServices.filter((p) => p.id !== id),
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_value_proposition')}
                tooltip={t('settings:settings_ai_form_tooltip_the_specific_benefit_customers_get_from')}
              />
              <Textarea
                placeholder={t('settings:settings_ai_form_placeholder_eg_save_10_hours_per_week')}
                value={formData.valueProposition}
                onChange={(e) => onChange({ ...formData, valueProposition: e.target.value })}
                rows={2}
              />
            </div>

            <TagInput
              label={t('settings:settings_ai_form_label_key_differentiators')}
              placeholder={t('settings:settings_ai_form_placeholder_type_and_press_enter')}
              tags={formData.keyDifferentiators}
              onChange={(tags) => onChange({ ...formData, keyDifferentiators: tags })}
              helperText={t('settings:settings_ai_form_helperText_what_makes_you_stand_out_press')}
            />

            {/* Competitors */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('settings:settings_ai_profile_competitors')}</label>
                  <p className="text-xs text-gray-500 mt-0.5">{t('settings:settings_ai_profile_competitors_hint')}</p>
                </div>
                <button
                  onClick={() => setCompetitorModal(true)}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <Icon name={ICONS.PLUS} size="xs" ariaHidden />
                  {t('settings:settings_ai_profile_add_competitor')}
                </button>
              </div>
              {formData.competitors.length > 0 && (
                <div className="space-y-2">
                  {formData.competitors.map((comp, idx) => (
                    <CompetitorCard
                      key={idx}
                      item={comp}
                      onDelete={(name) =>
                        onChange({
                          ...formData,
                          competitors: formData.competitors.filter((c) => c.name !== name),
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            <Select
              label={t('settings:settings_ai_form_label_price_positioning')}
              options={PRICE_POSITIONING_OPTIONS.map((o) => ({ ...o, label: t(`settings:settings_ai_opt_price_${o.value}`) }))}
              value={formData.pricePositioning}
              onValueChange={(value) => onChange({ ...formData, pricePositioning: value as PricePositioning })}
            />
          </>
        )}

        {/* STEP 4: Values */}
        {step === 3 && (
          <>
            {/* Core Values */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('settings:settings_ai_profile_core_values')}</label>
                  <p className="text-xs text-gray-500 mt-0.5">{t('settings:settings_ai_profile_core_values_hint')}</p>
                </div>
                <button
                  onClick={() => setCoreValueModal(true)}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <Icon name={ICONS.PLUS} size="xs" ariaHidden />
                  {t('settings:settings_ai_profile_add_core_value')}
                </button>
              </div>
              {formData.coreValues.length > 0 && (
                <div className="space-y-2">
                  {formData.coreValues.map((val, idx) => (
                    <CoreValueCard
                      key={idx}
                      item={val}
                      onDelete={(value) =>
                        onChange({
                          ...formData,
                          coreValues: formData.coreValues.filter((v) => v.value !== value),
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Brand Personality */}
            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_brand_personality_traits')}
                tooltip={t('settings:settings_ai_form_tooltip_personality_archetypes_that_describe_how_your')}
              />
              <p className="text-xs text-gray-500 mb-2">{t('settings:settings_ai_profile_personality_traits_hint')}</p>
              <PersonalityTraitsPicker
                selected={formData.brandPersonality}
                onChange={(traits) => onChange({ ...formData, brandPersonality: traits })}
              />
            </div>

            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_brand_principles')}
                tooltip={t('settings:settings_ai_form_tooltip_specific_guidelines_for_how_your_brand')}
              />
              <Textarea
                placeholder={t('settings:settings_ai_form_placeholder_eg_we_always_respond_within_24')}
                value={formData.brandPrinciples}
                onChange={(e) => onChange({ ...formData, brandPrinciples: e.target.value })}
                rows={3}
              />
            </div>
          </>
        )}

        {/* STEP 5: Voice */}
        {step === 4 && (
          <>
            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_brand_voice')}
                tooltip={t('settings:settings_ai_form_tooltip_a_written_description_of_how_your')}
              />
              <Textarea
                placeholder={t('settings:settings_ai_form_placeholder_eg_we_speak_like_a_knowledgeable')}
                value={formData.brandVoice}
                onChange={(e) => onChange({ ...formData, brandVoice: e.target.value })}
                rows={3}
              />
            </div>

            {/* Voice Characteristics */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:settings_ai_profile_voice_characteristics')}</label>
              <p className="text-xs text-gray-500 mb-2">{t('settings:settings_ai_profile_personality_traits_hint')}</p>
              <div className="grid grid-cols-2 gap-2">
                {VOICE_CHARACTERISTICS_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={cn(
                      'flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors',
                      formData.voiceCharacteristics.includes(opt.value)
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Checkbox
                      checked={formData.voiceCharacteristics.includes(opt.value)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onChange({
                            ...formData,
                            voiceCharacteristics: [...formData.voiceCharacteristics, opt.value],
                          });
                        } else {
                          onChange({
                            ...formData,
                            voiceCharacteristics: formData.voiceCharacteristics.filter((v) => v !== opt.value),
                          });
                        }
                      }}
                      inline
                    />
                    <span className="text-sm text-gray-700">{t(`settings:settings_ai_opt_voice_${opt.value}`)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_emotional_tone')}
                tooltip={t('settings:settings_ai_form_tooltip_the_primary_emotion_you_want_your')}
              />
              <Select
                options={EMOTIONAL_TONE_OPTIONS.map((o) => ({ ...o, label: t(`settings:settings_ai_opt_emotion_${o.value}`) }))}
                value={formData.emotionalTone || ''}
                onValueChange={(value) => onChange({ ...formData, emotionalTone: value as EmotionalTone })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:settings_ai_profile_writing_style_label')}</label>
              <p className="text-xs text-gray-500 mb-3">{t('settings:settings_ai_profile_writing_style_hint')}</p>
              <WritingStyleForm
                writingStyle={formData.writingStyle}
                onChange={(style) => onChange({ ...formData, writingStyle: style })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Select
                label={t('settings:settings_ai_profile_tone_label')}
                options={TONE_OPTIONS.map((o) => ({ ...o, label: t(`settings:settings_ai_opt_tone_${o.value}`) }))}
                value={formData.tone}
                onValueChange={(value) => onChange({ ...formData, tone: value })}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:settings_ai_profile_language_label')}</label>
                <Input
                  placeholder={t('settings:settings_ai_form_placeholder_eg_english_us_spanish_german_french')}
                  value={formData.language}
                  onChange={(e) => onChange({ ...formData, language: e.target.value })}
                />
              </div>
            </div>

            {/* Formality Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings:settings_ai_profile_formality_label')}</label>
              <div className="relative pt-1">
                <style>{`
                  .formality-slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background: var(--color-primary-500, #69C7C1);
                    cursor: pointer;
                    border: 2px solid white;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
                  }
                  .formality-slider::-moz-range-thumb {
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background: var(--color-primary-500, #69C7C1);
                    cursor: pointer;
                    border: 2px solid white;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
                  }
                `}</style>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={formData.formality}
                  onChange={(e) => onChange({ ...formData, formality: parseInt(e.target.value, 10) })}
                  className="formality-slider w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, var(--color-primary-500, #69C7C1) 0%, var(--color-primary-500, #69C7C1) ${formData.formality * 10}%, #e5e7eb ${formData.formality * 10}%, #e5e7eb 100%)`,
                  }}
                />
                <div className="flex justify-between mt-1.5">
                  <span className="text-xs text-gray-400">{t('settings:settings_ai_profile_casual')}</span>
                  <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                    {formData.formality}
                  </span>
                  <span className="text-xs text-gray-400">{t('settings:settings_ai_profile_professional')}</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* STEP 6: Guidelines (Nope List) */}
        {step === 5 && (
          <>
            <TagInput
              label={t('settings:settings_ai_form_label_avoid_words')}
              placeholder={t('settings:settings_ai_form_placeholder_type_words_to_avoid_and_press')}
              tags={formData.avoidWords}
              onChange={(tags) => onChange({ ...formData, avoidWords: tags })}
              helperText={t('settings:settings_ai_form_helperText_words_your_brand_should_never_use')}
            />

            {/* Avoid Topics */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('settings:settings_ai_profile_topics_to_avoid')}</label>
                  <p className="text-xs text-gray-500 mt-0.5">{t('settings:settings_ai_profile_topics_to_avoid_hint')}</p>
                </div>
                <button
                  onClick={() => setAvoidTopicModal(true)}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <Icon name={ICONS.PLUS} size="xs" ariaHidden />
                  {t('settings:settings_ai_profile_add_topic')}
                </button>
              </div>
              {formData.avoidTopics.length > 0 && (
                <div className="space-y-2">
                  {formData.avoidTopics.map((topic, idx) => (
                    <AvoidTopicCard
                      key={idx}
                      topic={topic}
                      onDelete={(topicName) =>
                        onChange({
                          ...formData,
                          avoidTopics: formData.avoidTopics.filter((t) => t.topic !== topicName),
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            <TagInput
              label={t('settings:settings_ai_form_label_sensitive_subjects')}
              placeholder={t('settings:settings_ai_form_placeholder_type_and_press_enter')}
              tags={formData.sensitiveSubjects}
              onChange={(tags) => onChange({ ...formData, sensitiveSubjects: tags })}
              helperText={t('settings:settings_ai_form_helperText_topics_requiring_extra_care_ai_will')}
            />

            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_competitor_mention_policy')}
                tooltip={t('settings:settings_ai_form_tooltip_how_should_ai_handle_mentions_of')}
              />
              <Select
                options={COMPETITOR_POLICY_OPTIONS.map((o) => ({ ...o, label: t(`settings:settings_ai_opt_policy_${o.value}`) }))}
                value={formData.competitorMentionPolicy}
                onValueChange={(value) =>
                  onChange({ ...formData, competitorMentionPolicy: value as CompetitorMentionPolicy })
                }
              />
            </div>

            <div>
              <LabelWithTooltip
                label={t('settings:settings_ai_form_label_content_restrictions')}
                tooltip={t('settings:settings_ai_form_tooltip_any_other_rules_or_limitations_for')}
              />
              <Textarea
                placeholder={t('settings:settings_ai_form_placeholder_eg_never_make_medical_claims_always')}
                value={formData.contentRestrictions}
                onChange={(e) => onChange({ ...formData, contentRestrictions: e.target.value })}
                rows={3}
              />
            </div>

            {/* Content Preferences */}
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('settings:settings_ai_profile_content_preferences')}</h4>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <Select
                  label={t('settings:settings_ai_form_label_default_length')}
                  options={LENGTH_OPTIONS.map((o) => ({ ...o, label: t(`settings:settings_ai_opt_length_${o.value}`) }))}
                  value={formData.defaultLength}
                  onValueChange={(value) => onChange({ ...formData, defaultLength: value as DefaultLength })}
                />
                <Select
                  label={t('settings:settings_ai_form_label_hashtag_style')}
                  options={HASHTAG_STYLE_OPTIONS.map((o) => ({ ...o, label: t(`settings:settings_ai_opt_hashtag_${o.value}`) }))}
                  value={formData.hashtagStyle}
                  onValueChange={(value) => onChange({ ...formData, hashtagStyle: value as HashtagStyle })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <label
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                    formData.includeEmojis
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  <Checkbox
                    checked={formData.includeEmojis}
                    onCheckedChange={(checked) => onChange({ ...formData, includeEmojis: checked })}
                    inline
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('settings:settings_ai_profile_include_emojis')}</span>
                    <span className="block text-xs text-gray-400">{t('settings:settings_ai_profile_include_emojis_hint')}</span>
                  </div>
                </label>

                <label
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                    formData.includeHashtags
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  <Checkbox
                    checked={formData.includeHashtags}
                    onCheckedChange={(checked) => onChange({ ...formData, includeHashtags: checked })}
                    inline
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('settings:settings_ai_profile_include_hashtags')}</span>
                    <span className="block text-xs text-gray-400">{t('settings:settings_ai_profile_include_hashtags_hint')}</span>
                  </div>
                </label>
              </div>

              <div>
                <LabelWithTooltip
                  label={t('settings:settings_ai_form_label_default_calltoaction')}
                  tooltip={t('settings:settings_ai_form_tooltip_the_action_you_most_commonly_want')}
                />
                <Input
                  placeholder={t('settings:settings_ai_form_placeholder_eg_start_your_free_trial_book')}
                  value={formData.defaultCta}
                  onChange={(e) => onChange({ ...formData, defaultCta: e.target.value })}
                />
              </div>
            </div>
          </>
        )}

      {/* Add Audience Modal */}
      <Modal
        open={audienceModal}
        onOpenChange={setAudienceModal}
        title={t('settings:settings_ai_form_title_add_audience')}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setAudienceModal(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              {t('settings:settings_ai_profile_cancel')}
            </button>
            <button
              onClick={addAudience}
              disabled={!tempAudience.name.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {t('settings:settings_ai_profile_add')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('settings:settings_ai_form_label_audience_name')}
            placeholder={t('settings:settings_ai_form_placeholder_eg_techsavvy_millennials_csuite_executives_budgetconscious')}
            value={tempAudience.name}
            onChange={(e) => setTempAudience({ ...tempAudience, name: e.target.value })}
            required
          />
          <div>
            <LabelWithTooltip
              label={t('settings:settings_ai_form_label_territories')}
              tooltip={t('settings:settings_ai_form_tooltip_geographic_regions_where_this_audience_is')}
            />
            <TagInput
              label=""
              placeholder={t('settings:settings_ai_form_placeholder_eg_usa_western_europe_apac')}
              tags={tempAudience.territories}
              onChange={(tags) => setTempAudience({ ...tempAudience, territories: tags })}
            />
          </div>
          <div>
            <LabelWithTooltip
              label={t('settings:settings_ai_form_label_preferred_platforms')}
              tooltip={t('settings:settings_ai_form_tooltip_social_platforms_this_audience_uses_most')}
            />
            <TagInput
              label=""
              placeholder={t('settings:settings_ai_form_placeholder_eg_linkedin_instagram_tiktok')}
              tags={tempAudience.preferredPlatforms}
              onChange={(tags) => setTempAudience({ ...tempAudience, preferredPlatforms: tags })}
            />
          </div>
        </div>
      </Modal>

      {/* Add Product/Service Modal */}
      <Modal
        open={productModal}
        onOpenChange={setProductModal}
        title={t('settings:settings_ai_form_title_add_productservice')}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setProductModal(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={addProduct}
              disabled={!tempProduct.name.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {t('common:add')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('settings:settings_ai_form_label_name')}
            placeholder={t('settings:settings_ai_form_placeholder_eg_social_media_scheduler_ai_caption')}
            value={tempProduct.name}
            onChange={(e) => setTempProduct({ ...tempProduct, name: e.target.value })}
            required
          />
          <Input
            label={t('settings:settings_ai_form_label_category')}
            placeholder={t('settings:settings_ai_form_placeholder_eg_software_service')}
            value={tempProduct.category || ''}
            onChange={(e) => setTempProduct({ ...tempProduct, category: e.target.value })}
          />
          <Textarea
            label={t('settings:settings_ai_form_label_description')}
            placeholder={t('settings:settings_ai_form_placeholder_brief_description_of_what_this_productservice')}
            value={tempProduct.description || ''}
            onChange={(e) => setTempProduct({ ...tempProduct, description: e.target.value })}
            rows={2}
          />
        </div>
      </Modal>

      {/* Add Competitor Modal */}
      <Modal
        open={competitorModal}
        onOpenChange={setCompetitorModal}
        title={t('settings:settings_ai_form_title_add_competitor')}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setCompetitorModal(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={addCompetitor}
              disabled={!tempCompetitor.name.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {t('common:add')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('settings:settings_ai_form_label_competitor_name')}
            placeholder={t('settings:settings_ai_form_placeholder_eg_buffer_hootsuite_sprout_social')}
            value={tempCompetitor.name}
            onChange={(e) => setTempCompetitor({ ...tempCompetitor, name: e.target.value })}
            required
          />
          <Textarea
            label={t('settings:settings_ai_form_label_positioning')}
            placeholder={t('settings:settings_ai_form_placeholder_eg_enterprisefocused_premium_pricing_limited_ai')}
            value={tempCompetitor.positioning || ''}
            onChange={(e) => setTempCompetitor({ ...tempCompetitor, positioning: e.target.value })}
            rows={2}
          />
        </div>
      </Modal>

      {/* Add Core Value Modal */}
      <Modal
        open={coreValueModal}
        onOpenChange={setCoreValueModal}
        title={t('settings:settings_ai_form_title_add_core_value')}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setCoreValueModal(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={addCoreValue}
              disabled={!tempCoreValue.value.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {t('common:add')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('settings:settings_ai_form_label_value')}
            placeholder={t('settings:settings_ai_form_placeholder_eg_transparency_innovation_customerfirst')}
            value={tempCoreValue.value}
            onChange={(e) => setTempCoreValue({ ...tempCoreValue, value: e.target.value })}
            required
          />
          <Textarea
            label={t('settings:settings_ai_form_label_description')}
            placeholder={t('settings:settings_ai_form_placeholder_eg_we_believe_in_being_upfront')}
            value={tempCoreValue.description || ''}
            onChange={(e) => setTempCoreValue({ ...tempCoreValue, description: e.target.value })}
            rows={2}
          />
        </div>
      </Modal>

      {/* Add Avoid Topic Modal */}
      <Modal
        open={avoidTopicModal}
        onOpenChange={setAvoidTopicModal}
        title={t('settings:settings_ai_form_title_add_topic_to_avoid')}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setAvoidTopicModal(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={addAvoidTopic}
              disabled={!tempAvoidTopic.topic.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {t('common:add')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('settings:settings_ai_form_label_topic')}
            placeholder={t('settings:settings_ai_form_placeholder_eg_politics_religion_competitor_comparisons')}
            value={tempAvoidTopic.topic}
            onChange={(e) => setTempAvoidTopic({ ...tempAvoidTopic, topic: e.target.value })}
            required
          />
          <Textarea
            label={t('settings:settings_ai_form_label_reason')}
            placeholder={t('settings:settings_ai_form_placeholder_eg_we_maintain_brand_neutrality_on')}
            value={tempAvoidTopic.reason || ''}
            onChange={(e) => setTempAvoidTopic({ ...tempAvoidTopic, reason: e.target.value })}
            rows={2}
          />
        </div>
      </Modal>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

interface Notification {
  type: 'success' | 'error';
  message: string;
}

export const AIBrandProfileSettings = () => {
  const { t } = useTranslation(['settings', 'common']);
  const [notification, setNotification] = useState<Notification | null>(null);

  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; profile: AIBrandProfile | null }>({
    open: false,
    profile: null,
  });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; profile: AIBrandProfile | null }>({
    open: false,
    profile: null,
  });
  const [versionHistoryModal, setVersionHistoryModal] = useState<{ open: boolean; profile: AIBrandProfile | null }>({
    open: false,
    profile: null,
  });
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Hooks
  const { profiles, isLoading, error } = useAIBrandProfiles();
  const deleteMutation = useDeleteAIBrandProfile();
  const setDefaultMutation = useSetDefaultAIBrandProfile();
  const activateMutation = useActivateAIBrandProfile();
  const duplicateMutation = useDuplicateAIBrandProfile();
  const { data: draft } = useBrandProfileDraft();
  const deleteDraftMutation = useDeleteBrandProfileDraft();
  const { formatRelativeDate } = useTimezone();
  // Plan gating (brand_world): existing Brand Worlds stay readable; create / edit / duplicate / activate /
  // default / restore are locked (the click opens the upsell). Delete stays available.
  const brandWorldLock = useFeatureLock('brand_world');
  // Effective limit (system cap 10, lowered by the package limit in enforce) from the shared create gate
  const { canCreateMore, atPackageLimit, atSystemCap, showLimitUpsell: handleLimitUpsell } = useBrandProfileCreateGate();

  // Auto-hide notifications
  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(null), 5000);
    return () => clearTimeout(timer);
  }, [notification]);

  // Handlers
  const handleCreate = brandWorldLock.guard(() => {
    setCreateModal(true);
  });

  const handleEdit = brandWorldLock.guard((profile: AIBrandProfile) => {
    setEditModal({ open: true, profile });
  });

  const handleDelete = (profile: AIBrandProfile) => {
    setDeleteModal({ open: true, profile });
  };

  const confirmDelete = () => {
    if (!deleteModal.profile) return;

    deleteMutation.mutate(deleteModal.profile.id, {
      onSuccess: () => {
        setDeleteModal({ open: false, profile: null });
      },
    });
  };

  const handleSetDefault = brandWorldLock.guard((profile: AIBrandProfile) => {
    setDefaultMutation.mutate(profile.id);
  });

  const handleActivate = brandWorldLock.guard((profile: AIBrandProfile) => {
    activateMutation.mutate(profile.id);
  });

  const handleDuplicate = brandWorldLock.guard((profile: AIBrandProfile) => {
    // A copy is a new profile: same doors as create (system cap: nothing is sent)
    if (atSystemCap) return;
    if (atPackageLimit) {
      handleLimitUpsell();
      return;
    }
    duplicateMutation.mutate(profile.id);
  });

  const handleOpenVersionHistory = (profile: AIBrandProfile) => {
    setVersionHistoryModal({ open: true, profile });
  };

  const handleResumeDraft = brandWorldLock.guard(() => {
    // A draft is a future profile: same doors as create (upsell at a package limit, nothing at the system cap)
    if (atSystemCap) return;
    if (atPackageLimit) {
      handleLimitUpsell();
      return;
    }
    // The create modal fetches the server draft itself and resumes at the saved step.
    setCreateModal(true);
  });

  const handleDiscardDraft = () => {
    setShowDiscardConfirm(true);
  };

  const confirmDiscardDraft = () => {
    deleteDraftMutation.mutate(undefined, {
      onSuccess: () => setShowDiscardConfirm(false),
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
        <p className="mt-4 font-medium text-gray-600">{t('status_loading')}</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <Icon name={ICONS.WARNING} size="lg" color="text-red-500" ariaHidden />
        </div>
        <p className="font-medium text-red-600">{t('error_generic')}</p>
        <p className="mt-1 text-sm text-gray-500">{t('error_refresh_page')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Notification Toast */}
      {notification && (
        <div
          className={cn(
            'fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+12px+var(--floating-stack-offset))] lg:bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl px-5 py-4 shadow-2xl',
            notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          )}
        >
          <Icon
            name={notification.type === 'success' ? ICONS.CHECK_CIRCLE : ICONS.CROSS_CIRCLE}
            size="sm"
            color="text-white"
            ariaHidden
          />
          <p className="font-medium">{notification.message}</p>
          <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
            <Icon name={ICONS.CROSS} size="sm" color="text-white/80" ariaHidden />
          </button>
        </div>
      )}

      {/* Header Card */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('settings:settings_ai_profile_title')}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {t('settings:settings_ai_profile_description')}
              </p>
            </div>
            {canCreateMore || atPackageLimit ? (
              <button
                onClick={canCreateMore ? handleCreate : handleLimitUpsell}
                className="self-start sm:self-auto flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                <Icon name={brandWorldLock.locked || atPackageLimit ? ICONS.LOCK : ICONS.PLUS} size="sm" ariaHidden />
                {t('settings:settings_ai_profile_create_profile')}
              </button>
            ) : (
              <div className="self-start sm:self-auto sm:text-right">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                  <Icon name={ICONS.WARNING} size="xs" ariaHidden />
                  {t('settings:settings_ai_profile_max_reached')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resume Draft Banner — server-side draft exists for the create wizard */}
      {draft && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 sm:px-5 py-3 sm:py-4">
          <div className="flex items-start gap-3">
            <Icon name={ICONS.SPARKLES} size="sm" color="text-primary-600" ariaHidden />
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {t('settings:settings_ai_profile_draft_banner_title')}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('settings:settings_ai_profile_draft_banner_updated', { time: formatRelativeDate(draft.updatedAt) })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              onClick={handleResumeDraft}
              disabled={atSystemCap}
              title={atSystemCap ? t('settings:settings_ai_profile_max_reached') : undefined}
              className="px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {t('settings:settings_ai_profile_draft_banner_resume')}
            </button>
            <button
              onClick={handleDiscardDraft}
              disabled={deleteDraftMutation.isPending}
              className="px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              {t('settings:settings_ai_profile_draft_banner_discard')}
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {profiles.length === 0 && (
        <div className="py-16 text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary-50">
            <Icon name={ICONS.SPARKLES} size="xl" color="text-primary-600" ariaHidden />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('settings:settings_ai_profile_empty_title')}</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
            {t('settings:settings_ai_profile_empty_description')}
          </p>
          <button
            onClick={canCreateMore ? handleCreate : handleLimitUpsell}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium text-primary-600-contrast bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            <Icon name={brandWorldLock.locked || atPackageLimit ? ICONS.LOCK : ICONS.PLUS} size="sm" ariaHidden />
            {t('settings:settings_ai_profile_create_profile')}
          </button>
          {brandWorldLock.locked && (
            <p className="mt-3 text-xs font-medium text-primary-700">{brandWorldLock.label}</p>
          )}
        </div>
      )}

      {/* Profiles Grid */}
      {profiles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {profiles.map((profile) => (
            <div
              key={profile.id}
              className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
              <div className="p-4 sm:p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="flex-1">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">{profile.name}</h3>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {profile.status === 'DRAFT' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                          <Icon name={ICONS.DRAFT} size="xs" ariaHidden />
                          {t('settings:settings_ai_profile_draft_badge', { defaultValue: 'Draft' })}
                        </span>
                      )}
                      {profile.isDefault && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-700 border border-primary-200">
                          <Icon name={ICONS.CHECK_CIRCLE} size="xs" ariaHidden />
                          {t('settings:settings_ai_profile_default')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    {profile.status === 'DRAFT' && (
                      <Tooltip content={brandWorldLock.locked ? brandWorldLock.label : t('settings:settings_ai_profile_activate', { defaultValue: 'Activate' })}>
                        <button
                          onClick={() => handleActivate(profile)}
                          disabled={activateMutation.isPending}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50"
                          aria-label={t('settings:settings_ai_profile_activate', { defaultValue: 'Activate' })}
                        >
                          <Icon name={brandWorldLock.locked ? ICONS.LOCK : ICONS.CHECK_CIRCLE} size="sm" ariaHidden />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip content={brandWorldLock.locked ? brandWorldLock.label : t('settings:settings_ai_profile_duplicate', { defaultValue: 'Duplicate' })}>
                      <button
                        onClick={() => handleDuplicate(profile)}
                        disabled={duplicateMutation.isPending || atSystemCap}
                        title={atSystemCap ? t('settings:settings_ai_profile_max_reached') : undefined}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                        aria-label={t('settings:settings_ai_profile_duplicate', { defaultValue: 'Duplicate' })}
                      >
                        <Icon name={brandWorldLock.locked ? ICONS.LOCK : ICONS.COPY} size="sm" ariaHidden />
                      </button>
                    </Tooltip>
                    <Tooltip content={t('settings:settings_ai_profile_version_history', { defaultValue: 'Version history' })}>
                      <button
                        onClick={() => handleOpenVersionHistory(profile)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        aria-label={t('settings:settings_ai_profile_version_history', { defaultValue: 'Version history' })}
                      >
                        <Icon name={ICONS.HISTORY} size="sm" ariaHidden />
                      </button>
                    </Tooltip>
                    <button
                      onClick={() => handleEdit(profile)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                      aria-label={t('settings:settings_ai_profile_edit')}
                      title={brandWorldLock.locked ? brandWorldLock.label : undefined}
                    >
                      <Icon name={brandWorldLock.locked ? ICONS.LOCK : ICONS.EDIT} size="sm" ariaHidden />
                    </button>
                    <button
                      onClick={() => handleDelete(profile)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      aria-label={t('settings:settings_ai_profile_delete')}
                    >
                      <Icon name={ICONS.TRASH} size="sm" ariaHidden />
                    </button>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  {profile.industry && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Icon name={ICONS.TAG} size="xs" color="text-gray-400" ariaHidden />
                      <span>{profile.industry}</span>
                    </div>
                  )}
                  {profile.audiences && profile.audiences.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Icon name={ICONS.USER} size="xs" color="text-gray-400" ariaHidden />
                      <span>{profile.audiences.length} {t('settings:settings_ai_profile_audience', { count: profile.audiences.length, defaultValue_one: 'audience', defaultValue_other: 'audiences' })}</span>
                    </div>
                  )}
                  {profile.brandPersonality && profile.brandPersonality.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Icon name={ICONS.SPARK} size="xs" color="text-gray-400" ariaHidden />
                      <span className="capitalize">{profile.brandPersonality.slice(0, 3).join(', ')}</span>
                    </div>
                  )}
                  {profile.language && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Icon name={ICONS.LANGUAGE} size="xs" color="text-gray-400" ariaHidden />
                      <span>{profile.language}</span>
                    </div>
                  )}
                  {/* Usage Stats */}
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Icon name={ICONS.CHART} size="xs" color="text-gray-400" ariaHidden />
                    <span>
                      {profile.usageCount === 0
                        ? t('settings:settings_ai_profile_no_used')
                        : t('settings:settings_ai_profile_used_times', { count: profile.usageCount })}
                    </span>
                  </div>
                  {profile.lastUsedAt && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Icon name={ICONS.CLOCK} size="xs" color="text-gray-400" ariaHidden />
                      <span>
                        {t('settings:settings_ai_profile_last_used', { date: formatRelativeDate(profile.lastUsedAt) })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions — "Set as default" only makes sense for ACTIVE profiles (the
                    server rejects setting a DRAFT as default) */}
                {!profile.isDefault && profile.status === 'ACTIVE' && (
                  <button
                    onClick={() => handleSetDefault(profile)}
                    disabled={setDefaultMutation.isPending}
                    className="w-full px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors disabled:opacity-50"
                  >
                    {brandWorldLock.locked && <Icon name={ICONS.LOCK} size="xs" className="mr-1.5 inline" ariaHidden />}
                    {t('settings:settings_ai_profile_set_as_default')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal — shared component with autosave */}
      <AIBrandProfileCreateModal
        open={createModal}
        onOpenChange={setCreateModal}
        onNotify={setNotification}
      />

      {/* Edit Modal — shared component */}
      <AIBrandProfileEditModal
        open={editModal.open}
        profile={editModal.profile}
        onOpenChange={(open) => setEditModal({ open, profile: open ? editModal.profile : null })}
        onNotify={setNotification}
      />

      {/* Version History Modal */}
      <AIBrandProfileVersionHistoryModal
        isOpen={versionHistoryModal.open}
        onClose={() => setVersionHistoryModal({ open: false, profile: null })}
        profileId={versionHistoryModal.profile?.id ?? null}
        profileName={versionHistoryModal.profile?.name}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteModal.open}
        onOpenChange={(open) => setDeleteModal({ open, profile: open ? deleteModal.profile : null })}
        title={t('settings:settings_ai_form_title_delete_profile')}
        description={t('settings:settings_ai_form_description_are_you_sure_you_want_to')}
        variant="danger"
        confirmText={t('settings:settings_ai_profile_delete')}
        cancelText={t('settings:settings_ai_profile_cancel')}
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Discard Draft Confirmation Modal */}
      <ConfirmationModal
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        title={t('settings:settings_ai_profile_discard_draft')}
        description={t('settings:settings_ai_profile_discard_draft_confirm')}
        variant="danger"
        confirmText={t('settings:settings_ai_profile_draft_banner_discard')}
        cancelText={t('common:cancel')}
        onConfirm={confirmDiscardDraft}
        isLoading={deleteDraftMutation.isPending}
      />
    </div>
  );
};
